import { useMemo, useState } from 'react';
import { Mail, MessageSquare, Search } from 'lucide-react';
import { EntityModal, EntityList, StatusBadge, FilterSelect } from '../common';
import type { EntityListColumn } from '../common';
import { useGuestMessages, useGuestMessageTemplates } from '../../hooks/useMessageLog';
import type { GuestMessageLogEntry } from '../../api/messageLog';

const STATUS_COLORS: Record<string, string> = {
  SENT: '#22c55e',
  SIMULATED: '#94a3b8',
  FAILED: '#ef4444',
};

const CHANNEL_OPTIONS = [
  { id: 'EMAIL', label: 'Email' },
  { id: 'SMS', label: 'SMS' },
];

const STATUS_OPTIONS = [
  { id: 'SENT', label: 'Sent' },
  { id: 'FAILED', label: 'Failed' },
  { id: 'SIMULATED', label: 'Simulated' },
];

const PAGE_SIZE = 25;

export interface GuestMessageLogProps {
  /** Scope to a whole event… */
  eventId?: string;
  /** …or to one guest. One of the two is required. */
  guestId?: string;
}

/**
 * Everything that was sent to guests — invitations, reminders, broadcasts,
 * RSVP confirmations — across email and SMS, with the message body.
 *
 * "Did they ever get the invitation?" is a question producers field
 * constantly, and answering it needs the actual text, the address it went to,
 * when, and whether it failed. Filters mirror how that question is asked: by
 * channel, by outcome, by which kind of message, by date, or by searching the
 * words someone remembers.
 */
export function GuestMessageLog({ eventId, guestId }: GuestMessageLogProps) {
  const [channel, setChannel] = useState<string[]>([]);
  const [status, setStatus] = useState<string[]>([]);
  const [template, setTemplate] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [viewing, setViewing] = useState<GuestMessageLogEntry | null>(null);

  const filters = useMemo(
    () => ({
      eventId,
      guestId,
      ...(channel.length ? { channel: channel as ('EMAIL' | 'SMS')[] } : {}),
      ...(status.length ? { status } : {}),
      ...(template.length ? { template } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(from ? { from: new Date(from).toISOString() } : {}),
      // Include the whole end day, not just its first instant.
      ...(to ? { to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [eventId, guestId, channel, status, template, search, from, to, page],
  );

  const { data, isLoading } = useGuestMessages(filters);
  const { data: templateData } = useGuestMessageTemplates(eventId);

  const templateOptions = useMemo(
    () =>
      (templateData?.templates ?? []).map((row) => ({
        id: row.template,
        label: `${row.template} (${row.count})`,
      })),
    [templateData],
  );

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns: EntityListColumn<GuestMessageLogEntry>[] = [
    {
      key: 'channel',
      header: '',
      render: (log) =>
        log.channel === 'SMS' ? (
          <MessageSquare className="w-4 h-4 text-gray-400" aria-label="SMS" />
        ) : (
          <Mail className="w-4 h-4 text-gray-400" aria-label="Email" />
        ),
    },
    {
      key: 'guest',
      header: 'To',
      render: (log) => (
        <div>
          <div className="font-medium text-gray-900">{log.guestName ?? '—'}</div>
          <div className="text-xs text-gray-500 break-all">{log.to}</div>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Message',
      render: (log) => (
        <div className="min-w-0">
          <div className="text-gray-900 truncate">{log.subject || '(no subject)'}</div>
          <div className="text-xs text-gray-500">{log.template}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (log) => (
        <div>
          <StatusBadge label={log.status} color={STATUS_COLORS[log.status]} />
          {log.errorMessage && (
            <div className="mt-0.5 text-xs text-red-600 truncate" title={log.errorMessage}>
              {log.errorMessage}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'sentAt',
      header: 'Sent',
      hideOnMobile: true,
      render: (log) => (
        <span className="text-sm text-gray-600">
          {new Date(log.sentAt).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </span>
      ),
    },
  ];

  const resetPageThen = <T,>(setter: (value: T) => void) => {
    return (value: T) => {
      setPage(0);
      setter(value);
    };
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => resetPageThen(setSearch)(e.target.value)}
            placeholder="Search recipient, subject or message text…"
            aria-label="Search messages"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <FilterSelect
          label="Channel"
          items={CHANNEL_OPTIONS}
          value={channel}
          onChange={(value) => resetPageThen(setChannel)(value as string[])}
          getValue={(option) => option.id}
          getLabel={(option) => option.label}
          multiple
        />
        <FilterSelect
          label="Status"
          items={STATUS_OPTIONS}
          value={status}
          onChange={(value) => resetPageThen(setStatus)(value as string[])}
          getValue={(option) => option.id}
          getLabel={(option) => option.label}
          multiple
        />
        {templateOptions.length > 0 && (
          <FilterSelect
            label="Type"
            items={templateOptions}
            value={template}
            onChange={(value) => resetPageThen(setTemplate)(value as string[])}
            getValue={(option) => option.id}
            getLabel={(option) => option.label}
            multiple
          />
        )}
        <label className="flex items-center gap-1 text-sm text-gray-600">
          <span className="sr-only">Sent from</span>
          <input
            type="date"
            value={from}
            aria-label="Sent from"
            onChange={(e) => resetPageThen(setFrom)(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
          />
        </label>
        <label className="flex items-center gap-1 text-sm text-gray-600">
          <span className="sr-only">Sent until</span>
          <input
            type="date"
            value={to}
            aria-label="Sent until"
            onChange={(e) => resetPageThen(setTo)(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
          />
        </label>
      </div>

      {!isLoading && logs.length === 0 ? (
        <p className="text-center py-8 text-sm text-gray-500">
          Nothing sent yet
          {search || channel.length || status.length ? ' matching these filters' : ''}.
        </p>
      ) : (
        <EntityList
          items={logs}
          columns={columns}
          getKey={(log) => log.id}
          isLoading={isLoading}
          onRowClick={setViewing}
        />
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= pageCount}
              className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <EntityModal
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing?.subject || 'Message'}
        size="lg"
      >
        {viewing && (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <dt className="text-gray-500">To</dt>
                <dd className="text-gray-900 break-all">{viewing.to}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Sent</dt>
                <dd className="text-gray-900">{new Date(viewing.sentAt).toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Channel</dt>
                <dd className="text-gray-900">{viewing.channel}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd className="text-gray-900">{viewing.status}</dd>
              </div>
            </dl>
            {viewing.errorMessage && (
              <p className="text-sm text-red-600" role="alert">
                {viewing.errorMessage}
              </p>
            )}
            {viewing.channel === 'SMS' ? (
              <pre className="whitespace-pre-wrap text-sm bg-gray-50 border border-gray-200 rounded-lg p-3">
                {viewing.body}
              </pre>
            ) : (
              // The stored HTML is our own template output, but it is rendered
              // in a sandboxed frame all the same — this view must never be a
              // way for message content to run script in the app.
              <iframe
                title="Message preview"
                sandbox=""
                srcDoc={viewing.body}
                className="w-full h-96 border border-gray-200 rounded-lg bg-white"
              />
            )}
          </div>
        )}
      </EntityModal>
    </div>
  );
}
